<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">My Payslips</h5>
        <p class="page-sub">Your payroll history and earnings breakdown</p>
      </div>
    </div>

    <div v-if="!employeeId" class="no-profile-box">
      <font-awesome-icon icon="user-slash" class="no-profile-icon" />
      <p class="no-profile-title">No employee profile linked</p>
      <p class="no-profile-sub">Contact your HR administrator to link your account to an employee record.</p>
    </div>

    <template v-else>

      <!-- Skeleton -->
      <div v-if="loading" class="sk-table">
        <div class="sk-row-head"></div>
        <div v-for="n in 6" :key="n" class="sk-row"></div>
      </div>

      <template v-else>
        <!-- Empty -->
        <div v-if="!payslips.length" class="empty-box">
          <font-awesome-icon icon="file-invoice-dollar" class="empty-icon" />
          <p class="empty-title">No payslips yet</p>
          <p class="empty-sub">Your payslips will appear here once payroll has been processed.</p>
        </div>

        <template v-else>
          <!-- Table -->
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Dates</th>
                  <th class="text-right">Gross</th>
                  <th class="text-right">Tax</th>
                  <th class="text-right">Pension</th>
                  <th class="text-right">Net Pay</th>
                  <th>Status</th>
                  <th class="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="slip in payslips" :key="slip.id" @click="viewSlip(slip)" class="tr-clickable">
                  <td>
                    <p class="cell-main">{{ slip.period?.name || '—' }}</p>
                  </td>
                  <td>
                    <span class="cell-sub">{{ fmtDate(slip.period?.start_date) }} – {{ fmtDate(slip.period?.end_date) }}</span>
                  </td>
                  <td class="text-right">
                    <span class="cell-main">{{ fmtMoney(slip.gross) }}</span>
                  </td>
                  <td class="text-right">
                    <span class="cell-sub">{{ fmtMoney(slip.tax) }}</span>
                  </td>
                  <td class="text-right">
                    <span class="cell-sub">{{ fmtMoney(slip.pension) }}</span>
                  </td>
                  <td class="text-right">
                    <span class="net-badge">{{ fmtMoney(slip.net) }}</span>
                  </td>
                  <td>
                    <span class="status-badge" :class="statusClass(slip.period?.status)">
                      {{ slip.period?.status || '—' }}
                    </span>
                  </td>
                  <td class="text-center">
                    <button class="btn-view" @click.stop="viewSlip(slip)" title="View payslip">
                      <font-awesome-icon icon="eye" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="pagination" v-if="meta.last_page > 1">
            <button class="pg-btn" :disabled="meta.current_page <= 1" @click="goPage(meta.current_page - 1)">
              <font-awesome-icon icon="chevron-left" />
            </button>
            <span class="pg-info">Page {{ meta.current_page }} of {{ meta.last_page }}</span>
            <button class="pg-btn" :disabled="meta.current_page >= meta.last_page" @click="goPage(meta.current_page + 1)">
              <font-awesome-icon icon="chevron-right" />
            </button>
          </div>
        </template>
      </template>
    </template>

  </div>
</template>

<script>
import axios from 'axios';
import { useStore } from 'vuex';

export default {
  name: 'MyPayslipsPage',
  data() {
    return {
      store: useStore(),
      loading: false,
      payslips: [],
      meta: { current_page: 1, last_page: 1 },
    };
  },
  computed: {
    employeeId() {
      return this.store.state.getLoggedInUser?.employee_id || null;
    },
  },
  mounted() {
    if (this.employeeId) this.fetchPayslips();
  },
  methods: {
    async fetchPayslips(page = 1) {
      this.loading = true;
      try {
        const { data } = await axios.get('/hr/self-service/payslips', { params: { page, length: 10 } });
        const d = data.data || {};
        this.payslips = d.data || [];
        this.meta = { current_page: d.current_page || 1, last_page: d.last_page || 1 };
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to load payslips');
      } finally {
        this.loading = false;
      }
    },
    goPage(p) { this.fetchPayslips(p); },
    viewSlip(slip) { this.$router.push(this.$url(`/payslip/${slip.id}`)); },
    statusClass(s) {
      if (!s) return 'badge--gray';
      const m = { open: 'badge--blue', closed: 'badge--green', locked: 'badge--orange' };
      return m[s.toLowerCase()] || 'badge--gray';
    },
    fmtMoney(v) {
      if (v == null) return '—';
      return Number(v).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    fmtDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
  },
};
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.no-profile-box   { display:flex; flex-direction:column; align-items:center; padding:80px 20px; gap:16px; }
.no-profile-icon  { font-size:48px; color:#dde5f0; }
.no-profile-title { font-size:18px; font-weight:700; color:#1a2b45; margin:0; }
.no-profile-sub   { font-size:13px; color:#7a90b0; margin:0; text-align:center; max-width:360px; }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row-head,.sk-row { height:44px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; margin-bottom:1px; }
.sk-row-head { height:40px; background:#f8fafc; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

.empty-box   { display:flex; flex-direction:column; align-items:center; padding:60px 20px; gap:12px; }
.empty-icon  { font-size:44px; color:#dde5f0; }
.empty-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.empty-sub   { font-size:13px; color:#7a90b0; margin:0; text-align:center; max-width:340px; }

.table-wrap { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.data-table { width:100%; border-collapse:collapse; font-size:13px; }
.data-table thead th { padding:12px 16px; background:#f8fafc; color:#7a90b0; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody td { padding:12px 16px; border-bottom:1px solid #f0f4f8; vertical-align:middle; }
.data-table tbody tr:last-child td { border-bottom:none; }
.tr-clickable { cursor:pointer; transition:background .1s; }
.tr-clickable:hover { background:#f8fafc; }
.text-right  { text-align:right; }
.text-center { text-align:center; }

.cell-main { font-size:13px; font-weight:700; color:#1a2b45; margin:0; }
.cell-sub  { font-size:12px; color:#7a90b0; }
.net-badge { font-size:13px; font-weight:700; color:#16a34a; background:#dcfce7; padding:3px 8px; border-radius:6px; }

.status-badge  { display:inline-block; font-size:11px; font-weight:700; padding:3px 10px; border-radius:20px; text-transform:capitalize; }
.badge--blue   { background:#dbeafe; color:#1d4ed8; }
.badge--green  { background:#dcfce7; color:#16a34a; }
.badge--orange { background:#fef3c7; color:#b45309; }
.badge--gray   { background:#f1f5f9; color:#64748b; }

.btn-view { width:30px; height:30px; border-radius:8px; border:1px solid #e4eaf2; background:#fff; color:#7a90b0; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; transition:all .15s; }
.btn-view:hover { background:#004AAD; color:#fff; border-color:#004AAD; }

.pagination { display:flex; align-items:center; justify-content:flex-end; gap:8px; padding:12px 0 0; }
.pg-btn     { width:32px; height:32px; border-radius:8px; border:1px solid #e4eaf2; background:#fff; color:#1a2b45; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; font-size:12px; transition:all .15s; }
.pg-btn:disabled { opacity:.4; cursor:not-allowed; }
.pg-btn:not(:disabled):hover { background:#004AAD; color:#fff; border-color:#004AAD; }
.pg-info    { font-size:12px; color:#7a90b0; font-weight:600; }

</style>
